import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import NotificationStatusChip, { notificationLabel } from '../messages/NotificationStatusChip';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SaveIcon from '@mui/icons-material/SaveAlt';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { trTR } from '@mui/x-date-pickers/locales';
import trLocale from 'date-fns/locale/tr';

import { fetchJson, getApiBase } from '../../apiBase';
import ExamCommunicationStatusPanel, { formatSendStats } from './ExamCommunicationStatusPanel';

const api = () => {
  const base = `${getApiBase()}/admin`;
  return {
    list: (school) => `${base}/reading-speed-tests/${encodeURIComponent(school)}`,
    create: (school) => `${base}/reading-speed-tests/${encodeURIComponent(school)}`,
    detail: (school, testId) => `${base}/reading-speed-tests/${encodeURIComponent(school)}/${encodeURIComponent(testId)}`,
    update: (school, testId) => `${base}/reading-speed-tests/${encodeURIComponent(school)}/${encodeURIComponent(testId)}`,
    delete: (school, testId) => `${base}/reading-speed-tests/${encodeURIComponent(school)}/${encodeURIComponent(testId)}`,
    sendPlan: (school, testId) => `${base}/reading-speed-tests/${encodeURIComponent(school)}/${encodeURIComponent(testId)}/send-plan`,
    save: (school, testId) => `${base}/reading-speed-tests/${encodeURIComponent(school)}/${encodeURIComponent(testId)}/results`,
    send: (school, testId) => `${base}/reading-speed-tests/${encodeURIComponent(school)}/${encodeURIComponent(testId)}/send`,
    classes: (school) => `${base}/classes/${encodeURIComponent(school)}`,
  };
};

const humanDate = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleDateString('tr-TR');
  } catch (_) {
    return value;
  }
};

const RESULT_STATUS_ABSENT = 'absent';
const ABSENT_RESULT_TOKEN = '__absent__';

const hasScoreValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';
const normalizeResultStatus = (value) => (String(value || '').trim().toLowerCase() === RESULT_STATUS_ABSENT ? RESULT_STATUS_ABSENT : null);
const isAbsentDraftValue = (value) => String(value ?? '').trim() === ABSENT_RESULT_TOKEN;
const hasResultEntry = (value, resultStatus = null) => (
  normalizeResultStatus(resultStatus) === RESULT_STATUS_ABSENT || isAbsentDraftValue(value) || hasScoreValue(value)
);

const toPickerDate = (value) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return null;
  const [year, month, day] = String(value).split('-').map(Number);
  const result = new Date(year, month - 1, day);
  return Number.isNaN(result.getTime()) ? null : result;
};

const toIsoDate = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const decorateTest = (test) => {
  if (!test) return test;
  const suffix = test.exam_date ? ` • ${humanDate(test.exam_date)}` : '';
  return {
    ...test,
    label: `${test.title || 'İsimsiz'}${suffix}`,
  };
};

export default function ReadingSpeedTestManager({ schoolId, onError, onSuccess }) {
  const apis = useMemo(() => api(), []);
  const scoreInputRefs = useRef({});
  const emptyCreateForm = { title: '', exam_date: '', class_id: null, duration_seconds: '60', notes: '', reminder_enabled: true };
  const [tests, setTests] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [detail, setDetail] = useState({ rows: [], test: null });
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [sendingPlan, setSendingPlan] = useState(false);
  const [sending, setSending] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [classes, setClasses] = useState([]);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [createErrors, setCreateErrors] = useState({});
  const [createdTest, setCreatedTest] = useState(null);
  const [localAlert, setLocalAlert] = useState('');

  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', exam_date: '', class_id: null, duration_seconds: '60', notes: '', reminder_enabled: true });
  const [editErrors, setEditErrors] = useState({});
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const selectedCreateClass = useMemo(() => {
    if (!createForm.class_id) return null;
    return classes.find((c) => String(c?.id) === String(createForm.class_id)) || null;
  }, [classes, createForm.class_id]);

  const selectedEditClass = useMemo(() => {
    if (!editForm.class_id) return null;
    return classes.find((c) => String(c?.id) === String(editForm.class_id)) || null;
  }, [classes, editForm.class_id]);

  const rowMap = useMemo(() => {
    const map = new Map();
    (detail.rows || []).forEach((row) => map.set(row.student_id, row));
    return map;
  }, [detail.rows]);

  const draftRows = useMemo(() => {
    return (detail.rows || []).map((row) => {
      const override = Object.prototype.hasOwnProperty.call(edits, row.student_id)
        ? edits[row.student_id]
        : normalizeResultStatus(row?.result_status) === RESULT_STATUS_ABSENT
          ? ABSENT_RESULT_TOKEN
          : row.result_value ?? '';
      return {
        ...row,
        draftValue: String(override ?? ''),
      };
    });
  }, [detail.rows, edits]);

  const sortedDraftRows = useMemo(() => {
    return [...draftRows].sort((left, right) => {
      const leftFilled = hasResultEntry(left.result_value, left.result_status);
      const rightFilled = hasResultEntry(right.result_value, right.result_status);
      if (leftFilled !== rightFilled) {
        return leftFilled ? 1 : -1;
      }
      return String(left.student_name || '').localeCompare(String(right.student_name || ''), 'tr-TR');
    });
  }, [draftRows]);

  const hasChanges = useMemo(() => Object.keys(edits).length > 0, [edits]);

  const hasAnyResultValue = useMemo(() => {
    return (detail.rows || []).some((row) => hasResultEntry(row?.result_value, row?.result_status));
  }, [detail.rows]);

  const progressStats = useMemo(() => {
    const totalCount = draftRows.length;
    const enteredCount = draftRows.filter((row) => hasResultEntry(row.draftValue, row.result_status)).length;
    const missingCount = Math.max(totalCount - enteredCount, 0);
    const notifiedCount = (detail.rows || []).filter((row) => row?.notified).length;
    const queuedCount = (detail.rows || []).filter((row) => row?.notification_status === 'pending').length;
    const draftCount = Object.keys(edits).length;
    const completionPercent = totalCount ? Math.round((enteredCount / totalCount) * 100) : 0;

    return {
      totalCount,
      enteredCount,
      missingCount,
      notifiedCount,
      queuedCount,
      draftCount,
      completionPercent,
    };
  }, [detail.rows, draftRows, edits]);

  const nextStep = useMemo(() => {
    if (!detail.test) {
      return {
        severity: 'info',
        title: 'Sıradaki adım: test seçin',
        description: 'Listeden bir test seçin veya yeni test oluşturun.',
      };
    }
    if (hasChanges) {
      return {
        severity: 'warning',
        title: 'Sıradaki adım: değişiklikleri kaydedin',
        description: `${progressStats.draftCount} öğrenci için değişiklik var. Göndermeden önce kaydetmeniz gerekiyor.`,
      };
    }
    if (progressStats.enteredCount === 0) {
      return {
        severity: 'info',
        title: 'Sıradaki adım: sonuç girin',
        description: 'Önce en az bir öğrenci için sonuç girin. Eksik girişler tabloda en üstte gösterilir.',
      };
    }
    if (progressStats.missingCount > 0) {
      return {
        severity: 'warning',
        title: 'Sıradaki adım: eksik sonuçları tamamlayın',
        description: `${progressStats.missingCount} öğrencinin sonucu eksik. Hızlı tamamlamanız için eksik kayıtlar üstte sıralanıyor.`,
      };
    }
    if (!detail.test.result_message_last_sent_at) {
      return {
        severity: 'success',
        title: 'Sıradaki adım: sonuçları velilere gönderin',
        description: 'Tüm sonuçlar girildi. Sonuçları şimdi WhatsApp ile paylaşabilirsiniz.',
      };
    }
    return {
      severity: 'success',
      title: detail.test.result_message_status === 'sent' ? 'Süreç tamamlandı' : 'Gönderim durumu',
      description: notificationLabel(detail.test.result_message_status),
    };
  }, [detail.test, hasChanges, progressStats]);

  const resultDeliveryLabel = useMemo(() => {
    if (!detail.test) return 'Paylaşım bekleniyor';
    if (detail.test.result_message_status) return notificationLabel(detail.test.result_message_status);
    if (progressStats.queuedCount > 0) return `${progressStats.queuedCount} öğrenci kuyrukta`;
    if (progressStats.enteredCount > 0) return 'Gönderime hazır';
    return 'Sonuçlar hazırlanıyor';
  }, [detail.test, progressStats]);

  const loadClasses = useCallback(async () => {
    if (!schoolId) return;
    try {
      const res = await fetchJson(apis.classes(schoolId));
      if (res.ok) {
        const data = await res.json();
        const items = Array.isArray(data)
          ? data
              .map((c) => {
                if (!c) return null;
                if (typeof c === 'string') return { id: c, name: c };
                return { id: c?.id ?? c?.code ?? c?.name, name: c?.code ?? c?.name ?? String(c?.id ?? '') };
              })
              .filter(Boolean)
          : [];
        items.sort((a, b) => String(a?.name).localeCompare(String(b?.name), 'tr-TR'));
        setClasses(items);
      }
    } catch (err) {
      console.warn('classes load failed', err);
    }
  }, [apis, schoolId]);

  const loadTests = useCallback(
    async (focusTestId = null) => {
      if (!schoolId) return;
      setLoadingList(true);
      try {
        const res = await fetchJson(apis.list(schoolId));
        if (!res.ok) throw new Error('list_failed');
        const data = await res.json();
        const items = (data || []).map(decorateTest);
        setTests(items);
        setSelectedTest((prev) => {
          if (focusTestId) {
            return items.find((it) => String(it.id) === String(focusTestId)) || prev;
          }
          if (!prev && items.length) {
            return items[0];
          }
          if (prev) {
            const refreshed = items.find((it) => String(it.id) === String(prev.id));
            return refreshed || prev;
          }
          return prev;
        });
      } catch (err) {
        console.error(err);
        onError && onError('Test listesi yüklenemedi');
      } finally {
        setLoadingList(false);
      }
    },
    [apis, schoolId, onError]
  );

  const loadDetail = useCallback(
    async (test) => {
      if (!test || !schoolId) return;
      setLoadingDetail(true);
      try {
        const res = await fetchJson(apis.detail(schoolId, test.id));
        if (!res.ok) throw new Error('detail_failed');
        const data = await res.json();
        setDetail({ test: data.test, rows: data.rows || [] });
        setEdits({});
        setLocalAlert('');
      } catch (err) {
        console.error(err);
        onError && onError('Test detayı yüklenemedi');
        setDetail({ test, rows: [] });
      } finally {
        setLoadingDetail(false);
      }
    },
    [apis, schoolId, onError]
  );

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  useEffect(() => {
    loadTests();
  }, [loadTests]);

  useEffect(() => {
    if (selectedTest) {
      loadDetail(selectedTest);
    }
  }, [selectedTest, loadDetail]);

  const handleResultChange = (studentId, value) => {
    const row = rowMap.get(studentId);
    const original = normalizeResultStatus(row?.result_status) === RESULT_STATUS_ABSENT
      ? ABSENT_RESULT_TOKEN
      : String(row?.result_value ?? '').trim();
    const next = String(value ?? '');
    setEdits((prev) => {
      const copy = { ...prev };
      if (next.trim() === original) {
        delete copy[studentId];
      } else {
        copy[studentId] = value;
      }
      return copy;
    });
  };

  const handleAbsentToggle = (studentId) => {
    const row = rowMap.get(studentId);
    const original = normalizeResultStatus(row?.result_status) === RESULT_STATUS_ABSENT
      ? ABSENT_RESULT_TOKEN
      : String(row?.result_value ?? '').trim();

    setEdits((prev) => {
      const current = Object.prototype.hasOwnProperty.call(prev, studentId)
        ? String(prev[studentId] ?? '')
        : original;
      const next = current === ABSENT_RESULT_TOKEN ? '' : ABSENT_RESULT_TOKEN;
      const copy = { ...prev };
      if (next === original) {
        delete copy[studentId];
      } else {
        copy[studentId] = next;
      }
      return copy;
    });
  };

  const handleScoreKeyDown = (event, studentId) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();

    const currentIndex = sortedDraftRows.findIndex((row) => String(row.student_id) === String(studentId));
    if (currentIndex < 0) return;

    const nextRow = sortedDraftRows[currentIndex + 1];
    if (!nextRow) return;

    const nextInput = scoreInputRefs.current[nextRow.student_id];
    if (nextInput && typeof nextInput.focus === 'function') {
      nextInput.focus();
      if (typeof nextInput.select === 'function') {
        nextInput.select();
      }
    }
  };

  const handleSave = async () => {
    if (!selectedTest || !schoolId) return;
    setSaving(true);
    try {
      const payloadRows = draftRows.map((row) => {
        const override = Object.prototype.hasOwnProperty.call(edits, row.student_id)
          ? edits[row.student_id]
          : normalizeResultStatus(row?.result_status) === RESULT_STATUS_ABSENT
            ? ABSENT_RESULT_TOKEN
            : row.result_value ?? '';
        const resultValue = typeof override === 'string' ? override.trim() : override;
        const isAbsent = resultValue === ABSENT_RESULT_TOKEN;
        return {
          student_id: row.student_id,
          result_value: isAbsent ? null : (resultValue === '' ? null : resultValue),
          result_status: isAbsent ? RESULT_STATUS_ABSENT : null,
        };
      });
      const res = await fetchJson(apis.save(schoolId, selectedTest.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: payloadRows }),
      });
      if (!res.ok) throw new Error('save_failed');
      const data = await res.json();
      setDetail({ test: data.test, rows: data.rows || [] });
      setEdits({});
      setLocalAlert('Sonuçlar kaydedildi');
      onSuccess && onSuccess('Sonuçlar kaydedildi');
    } catch (err) {
      console.error(err);
      onError && onError('Sonuçlar kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!selectedTest || !schoolId) return;
    setSending(true);
    try {
      const res = await fetchJson(apis.send(schoolId, selectedTest.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('send_failed');
      const data = await res.json();
      setLocalAlert(`Kuyruğa eklendi • ${data.queued || 0} mesaj`);
      onSuccess && onSuccess('Sonuç bildirimleri kuyruğa eklendi');
      await loadDetail(selectedTest);
    } catch (err) {
      console.error(err);
      onError && onError('Mesajlar gönderilemedi');
    } finally {
      setSending(false);
    }
  };

  const sendPlanForTest = async (testId) => {
    const res = await fetchJson(apis.sendPlan(schoolId, testId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (!res.ok) throw new Error('send_plan_failed');
    return res.json();
  };

  const resetCreateState = () => {
    setCreateForm(emptyCreateForm);
    setCreateErrors({});
    setCreatedTest(null);
  };

  const hideCreate = () => {
    setShowCreate(false);
    resetCreateState();
  };

  const toggleCreate = () => {
    if (showCreate) {
      closeCreate();
      return;
    }
    resetCreateState();
    setShowCreate(true);
  };

  const closeCreate = () => {
    if (creating || sendingPlan) return;
    hideCreate();
  };

  const handleCreateTest = async () => {
    const nextErrors = {};
    if (!createForm.title?.trim()) {
      nextErrors.title = 'Başlık gerekli';
    }
    if (!String(createForm.exam_date || '').trim()) {
      nextErrors.exam_date = 'Tarih gerekli';
    }
    if (!createForm.class_id) {
      nextErrors.class_id = 'Şube gerekli';
    }
    setCreateErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      onError && onError(Object.values(nextErrors)[0]);
      return;
    }
    setCreating(true);
    try {
      const durationSecondsValue = String(createForm.duration_seconds ?? '').trim();
      const durationSeconds = durationSecondsValue ? Number.parseInt(durationSecondsValue, 10) : null;
      const res = await fetchJson(apis.create(schoolId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: createForm.title,
          exam_date: createForm.exam_date || null,
          class_id: createForm.class_id,
          duration_seconds: Number.isFinite(durationSeconds) ? durationSeconds : null,
          notes: createForm.notes || null,
          reminder_enabled: !!createForm.reminder_enabled,
        }),
      });
      if (!res.ok) throw new Error('create_failed');
      const test = await res.json();
      await loadTests(test.id);
      setCreatedTest({
        id: test.id,
        title: test.title || createForm.title,
        exam_date: test.exam_date || createForm.exam_date || '',
        class_label: selectedCreateClass?.name || '-',
        duration_seconds: Number.isFinite(durationSeconds) ? durationSeconds : null,
        reminder_enabled: !!createForm.reminder_enabled,
      });
      setLocalAlert('Yeni test oluşturuldu');
      onSuccess && onSuccess('Yeni test oluşturuldu');
    } catch (err) {
      console.error(err);
      onError && onError('Test oluşturulamadı');
    } finally {
      setCreating(false);
    }
  };

  const handleSendCreatedPlan = async () => {
    if (!createdTest || !schoolId) return;
    setSendingPlan(true);
    try {
      const stats = await sendPlanForTest(createdTest.id);
      setLocalAlert(`Test oluşturuldu ve plan kuyruğa eklendi • ${formatSendStats(stats)}`);
      onSuccess && onSuccess('Yeni test oluşturuldu ve plan bildirimleri kuyruğa eklendi');
      await loadTests(createdTest.id);
      hideCreate();
    } catch (err) {
      console.error(err);
      onError && onError('Plan mesajları gönderilemedi');
    } finally {
      setSendingPlan(false);
    }
  };

  const disableSend =
    !detail.rows?.some((row) => hasResultEntry(row.result_value, row.result_status)) || hasChanges || sending || sendingPlan;

  const disableEditDelete = !selectedTest || hasChanges || saving || sending || sendingPlan || loadingDetail;

  const openEdit = () => {
    if (!detail.test) return;
    setEditErrors({});
    setEditForm({
      title: detail.test.title || '',
      exam_date: detail.test.exam_date || '',
      class_id: detail.test.class_id ?? null,
      duration_seconds: String(detail.test.duration_seconds ?? 60),
      notes: detail.test.notes || '',
      reminder_enabled: detail.test.reminder_enabled !== false,
    });
    setShowEdit(true);
  };

  const closeEdit = () => {
    if (editing) return;
    setShowEdit(false);
    setEditErrors({});
  };

  const handleUpdateTest = async () => {
    if (!selectedTest || !schoolId) return;
    const nextErrors = {};
    if (!editForm.title?.trim()) {
      nextErrors.title = 'Başlık gerekli';
    }
    if (!String(editForm.exam_date || '').trim()) {
      nextErrors.exam_date = 'Tarih gerekli';
    }
    if (!editForm.class_id) {
      nextErrors.class_id = 'Şube gerekli';
    }
    setEditErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      onError && onError(Object.values(nextErrors)[0]);
      return;
    }

    setEditing(true);
    try {
      const durationSecondsValue = String(editForm.duration_seconds ?? '').trim();
      const durationSeconds = durationSecondsValue ? Number.parseInt(durationSecondsValue, 10) : null;
      const payload = {
        title: editForm.title,
        exam_date: editForm.exam_date || null,
        class_id: editForm.class_id,
        duration_seconds: Number.isFinite(durationSeconds) ? durationSeconds : null,
        notes: editForm.notes || null,
        reminder_enabled: !!editForm.reminder_enabled,
      };
      const res = await fetchJson(apis.update(schoolId, selectedTest.id), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('update_failed');
      setShowEdit(false);
      await loadTests(selectedTest.id);
      await loadDetail({ id: selectedTest.id });
      onSuccess && onSuccess('Test güncellendi');
    } catch (err) {
      console.error(err);
      onError && onError('Test güncellenemedi');
    } finally {
      setEditing(false);
    }
  };

  const openDelete = () => {
    if (!selectedTest) return;
    setShowDelete(true);
  };

  const closeDelete = () => {
    if (deleting) return;
    setShowDelete(false);
  };

  const handleDeleteTest = async () => {
    if (!selectedTest || !schoolId) return;
    setDeleting(true);
    try {
      const res = await fetchJson(apis.delete(schoolId, selectedTest.id), { method: 'DELETE' });
      if (!res.ok) throw new Error('delete_failed');
      setShowDelete(false);
      setSelectedTest(null);
      setDetail({ rows: [], test: null });
      setEdits({});
      await loadTests();
      onSuccess && onSuccess('Test silindi');
    } catch (err) {
      console.error(err);
      onError && onError('Test silinemedi');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={trLocale}
      localeText={trTR.components.MuiLocalizationProvider.defaultProps.localeText}
    >
    <Box>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Stack spacing={1.5}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs="auto" md="auto">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Tooltip title="Yeni Test">
                        <span>
                          <IconButton
                            onClick={toggleCreate}
                            size="small"
                            color="primary"
                            sx={{
                              display: { xs: 'inline-flex', md: 'none' },
                              border: '1px solid',
                              borderColor: 'primary.main',
                            }}
                          >
                            <AddCircleOutlineIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Button
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={toggleCreate}
                        size="small"
                        sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                      >
                        Yeni Test
                      </Button>
                    </Stack>
                  </Grid>
                  <Grid item xs md>
                    <Autocomplete
                      options={tests}
                      value={selectedTest || null}
                      loading={loadingList}
                      getOptionLabel={(option) => option?.label || ''}
                      onChange={(_, value) => setSelectedTest(value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Testler"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingList ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      disabled={loadingList}
                    />
                  </Grid>
                  <Grid item xs="auto" md="auto">
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                      <Tooltip title="Yenile">
                        <span>
                          <IconButton
                            onClick={() => {
                              loadTests();
                              if (selectedTest) loadDetail(selectedTest);
                            }}
                            disabled={loadingList || loadingDetail}
                            size="small"
                          >
                            <RefreshIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Düzenle">
                        <span>
                          <IconButton onClick={openEdit} disabled={disableEditDelete} size="small">
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Sil">
                        <span>
                          <IconButton onClick={openDelete} disabled={disableEditDelete} size="small">
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>

              {!loadingList && !tests.length ? (
                <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="body2">Henüz test oluşturulmadı. Yukarıdaki “Yeni Test” butonuyla başlayın.</Typography>
                </Box>
              ) : (
                <>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                      <Typography variant="overline" color="text.secondary">1. Test Tanımlama</Typography>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {detail.test?.title || 'Seçim bekleniyor'}
                      </Typography>
                      {detail.test ? (
                        <Stack spacing={1} sx={{ mt: 1 }}>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {detail.test.exam_date && (
                              <Chip size="small" label={humanDate(detail.test.exam_date)} />
                            )}
                            {detail.test.class_name && (
                              <Chip size="small" color="primary" label={detail.test.class_name} />
                            )}
                            {detail.test.duration_seconds != null && (
                              <Chip size="small" color="secondary" label={`${detail.test.duration_seconds} sn`} />
                            )}
                          </Stack>
                          {detail.test.notes && (
                            <Typography variant="body2" color="text.secondary">
                              <strong>Not:</strong> {detail.test.notes}
                            </Typography>
                          )}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Önce bir test seçin veya oluşturun.
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                      <Typography variant="overline" color="text.secondary">2. Sonuç Girişi</Typography>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {progressStats.enteredCount}/{progressStats.totalCount} öğrenci tamamlandı
                      </Typography>
                      <LinearProgress variant="determinate" value={progressStats.completionPercent} sx={{ my: 1, height: 8, borderRadius: 999 }} />
                      <Typography variant="body2" color="text.secondary">
                        {progressStats.missingCount > 0 ? `${progressStats.missingCount} öğrencinin sonucu eksik` : 'Tüm sonuçlar girildi'}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                      <Typography variant="overline" color="text.secondary">3. Veli Paylaşımı</Typography>
                      <Typography variant="subtitle2" fontWeight={700}>{resultDeliveryLabel}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {progressStats.notifiedCount > 0
                          ? `${progressStats.notifiedCount} öğrenciye gönderildi`
                          : progressStats.enteredCount > 0
                            ? 'Sonuçlar hazır. İsterseniz velilerle hemen paylaşabilirsiniz.'
                            : 'Önce sonuçları girin, sonra velilerle paylaşın.'}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {detail.test && (
                      <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle2" fontWeight={600}>{detail.test.title}</Typography>
                          <Alert severity={nextStep.severity} variant="outlined">
                            <Typography variant="subtitle2" fontWeight={700}>{nextStep.title}</Typography>
                            <Typography variant="body2">{nextStep.description}</Typography>
                          </Alert>
                          <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'rgba(15, 23, 42, 0.02)' }}>
                            <Stack spacing={0.75}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                İlerleme Özeti
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {progressStats.enteredCount}/{progressStats.totalCount} öğrenci için sonuç girildi
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {progressStats.notifiedCount > 0 ? `${progressStats.notifiedCount} öğrenciye sonuç iletildi` : 'Henüz sonuç paylaşılmadı'}
                              </Typography>
                            </Stack>
                          </Paper>
                          <ExamCommunicationStatusPanel item={detail.test} />
                          {localAlert && (
                            <Alert severity="success" variant="outlined" sx={{ mt: 1 }}>
                              {localAlert}
                            </Alert>
                          )}
                        </Stack>
                      </Paper>
                    )}
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {loadingDetail ? (
                      <Box sx={{ py: 6, textAlign: 'center' }}>
                        <CircularProgress size={32} />
                      </Box>
                    ) : !detail.rows?.length ? (
                      <Alert severity="info">Seçilen test için öğrenci bulunamadı.</Alert>
                    ) : (
                      <Stack spacing={1.5}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                            <Box>
                              <Typography variant="subtitle2" fontWeight={700}>Sonuç Girişi</Typography>
                              <Typography variant="body2" color="text.secondary">
                                Eksik sonuçlar üstte gösterilir. Tüm öğrencileri doldurunca paylaşım aşamasına geçebilirsiniz.
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                İpucu: Sonucu yazıp Enter'a bastığınızda bir sonraki öğrenciye geçebilirsiniz.
                              </Typography>
                            </Box>
                            <Stack spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-end' }}>
                              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                                <Chip size="small" label={`${progressStats.enteredCount}/${progressStats.totalCount} tamamlandı`} color="primary" variant="outlined" />
                                {progressStats.missingCount > 0 && (
                                  <Chip size="small" label={`${progressStats.missingCount} eksik`} color="warning" variant="outlined" />
                                )}
                                {hasChanges && (
                                  <Chip size="small" label={`${progressStats.draftCount} kaydedilmemiş`} color="warning" />
                                )}
                              </Stack>
                              <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  startIcon={<SaveIcon />}
                                  disabled={!hasChanges || saving}
                                  onClick={handleSave}
                                  size="small"
                                  sx={{ flex: { xs: 1, sm: 'initial' } }}
                                >
                                  Sonuçları Kaydet
                                </Button>
                                <Button
                                  variant="contained"
                                  color="success"
                                  startIcon={<WhatsAppIcon />}
                                  disabled={disableSend}
                                  onClick={handleSend}
                                  size="small"
                                  sx={{ flex: { xs: 1, sm: 'initial' } }}
                                >
                                  Sonuçları Gönder
                                </Button>
                              </Stack>
                            </Stack>
                          </Stack>
                        </Paper>
                        <TableContainer sx={{ maxHeight: 520 }}>
                          <Table
                            stickyHeader
                            size="small"
                            sx={{
                              '& .MuiTableCell-head': {
                                textTransform: 'uppercase',
                                fontSize: 12,
                                letterSpacing: 0.4,
                                color: 'text.secondary',
                                backgroundColor: 'background.default',
                              },
                              '& .MuiTableCell-root': {
                                borderBottomColor: 'rgba(0,0,0,0.04)',
                              },
                            }}
                          >
                            <TableHead>
                              <TableRow>
                                <TableCell width="40%">Öğrenci</TableCell>
                                <TableCell width="12%" align="center">Şube</TableCell>
                                <TableCell width="28%" align="center">Sözcük / dk</TableCell>
                                <TableCell width="20%" align="center">Süreç</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sortedDraftRows.map((row) => {
                                const value = Object.prototype.hasOwnProperty.call(edits, row.student_id)
                                  ? edits[row.student_id]
                                  : row.draftValue;
                                const isAbsent = isAbsentDraftValue(value);
                                return (
                                  <TableRow key={row.student_id} hover>
                                    <TableCell>
                                      <Typography variant="body2" fontWeight={600}>{row.student_name}</Typography>
                                      <Typography variant="caption" color="text.secondary">#{row.student_id}</Typography>
                                    </TableCell>
                                    <TableCell align="center">{row.class_name || '-'}</TableCell>
                                    <TableCell align="center">
                                      <Stack spacing={0.75} alignItems="center">
                                        <TextField
                                          size="small"
                                          fullWidth
                                          value={isAbsent ? '' : value}
                                          placeholder={isAbsent ? 'Girmedi' : 'Örn. 125'}
                                          disabled={isAbsent}
                                          onChange={(e) => handleResultChange(row.student_id, e.target.value)}
                                          onKeyDown={(event) => handleScoreKeyDown(event, row.student_id)}
                                          inputRef={(element) => {
                                            if (element) {
                                              scoreInputRefs.current[row.student_id] = element;
                                            } else {
                                              delete scoreInputRefs.current[row.student_id];
                                            }
                                          }}
                                          sx={{
                                            width: { xs: 75, sm: 100 },
                                            maxWidth: 260,
                                            '& input': {
                                              textAlign: 'center',
                                              fontWeight: 600,
                                              fontSize: '1.15rem',
                                            },
                                          }}
                                        />
                                        <Chip
                                          label="Sınava girmedi"
                                          size="small"
                                          color={isAbsent ? 'warning' : 'default'}
                                          variant={isAbsent ? 'filled' : 'outlined'}
                                          onClick={() => handleAbsentToggle(row.student_id)}
                                        />
                                      </Stack>
                                    </TableCell>
                                    <TableCell align="center">
                                      <NotificationStatusChip status={row.notification_status || (row.notified ? 'sent' : null)} />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1, width: '100%' }}>
                          <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<SaveIcon />}
                            disabled={!hasChanges || saving}
                            onClick={handleSave}
                            size="small"
                            sx={{ flex: { xs: 1, sm: 'initial' } }}
                          >
                            Sonuçları Kaydet
                          </Button>
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<WhatsAppIcon />}
                            disabled={disableSend}
                            onClick={handleSend}
                            size="small"
                            sx={{ flex: { xs: 1, sm: 'initial' } }}
                          >
                            Sonuçları Gönder
                          </Button>
                        </Stack>
                      </Stack>
                    )}
                  </Grid>
                </Grid>
                </>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={showCreate} onClose={closeCreate} fullWidth maxWidth="sm">
        <DialogTitle>{createdTest ? '2. Adım: Planı Gönder' : 'Yeni Test'}</DialogTitle>
        <DialogContent>
          {createdTest ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="success" variant="outlined">
                Test oluşturuldu. Şimdi plan mesajını velilere gönderme adımındasınız.
              </Alert>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" fontWeight={600}>{createdTest.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tarih: {createdTest.exam_date ? humanDate(createdTest.exam_date) : '-'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Şube: {createdTest.class_label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Süre: {createdTest.duration_seconds != null ? `${createdTest.duration_seconds} sn` : '-'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Hatırlatma: {createdTest.reminder_enabled ? 'Açık' : 'Kapalı'}
                  </Typography>
                </Stack>
              </Paper>
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Test başlığı"
                required
                fullWidth
                value={createForm.title}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setCreateForm((prev) => ({ ...prev, title: nextValue }));
                  setCreateErrors((prev) => ({ ...prev, title: nextValue.trim() ? null : prev.title }));
                }}
                error={Boolean(createErrors.title)}
                helperText={createErrors.title || ''}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
                <DatePicker
                  label="Tarih"
                  value={toPickerDate(createForm.exam_date)}
                  onChange={(value) => {
                    const nextValue = toIsoDate(value);
                    setCreateForm((prev) => ({ ...prev, exam_date: nextValue }));
                    setCreateErrors((prev) => ({ ...prev, exam_date: nextValue ? null : prev.exam_date }));
                  }}
                  format="dd.MM.yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: Boolean(createErrors.exam_date),
                      helperText: createErrors.exam_date || '',
                      sx: { minWidth: 160 },
                    },
                  }}
                />
                <Autocomplete
                  fullWidth
                  options={classes}
                  value={selectedCreateClass}
                  onChange={(_, value) => {
                    setCreateForm((prev) => ({ ...prev, class_id: value?.id ?? null }));
                    setCreateErrors((prev) => ({ ...prev, class_id: value?.id ? null : prev.class_id }));
                  }}
                  isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                  getOptionLabel={(opt) => String(opt?.name ?? '')}
                  renderInput={(params) => <TextField {...params} label="Şube" error={Boolean(createErrors.class_id)} helperText={createErrors.class_id || ''} />}
                />
              </Stack>
              <TextField
                label="Süre (sn)"
                fullWidth
                value={createForm.duration_seconds}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, duration_seconds: e.target.value }))}
              />
              <TextField
                label="Not"
                fullWidth
                multiline
                minRows={3}
                value={createForm.notes}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, notes: e.target.value }))}
              />
              <FormControlLabel
                control={<Switch checked={!!createForm.reminder_enabled} onChange={(e) => setCreateForm((prev) => ({ ...prev, reminder_enabled: e.target.checked }))} />}
                label="1 gün önce hatırlatma mesajı gönder"
              />
              <Alert severity="info" variant="outlined">
                İlk adımda test oluşturulur. Oluşturma sonrası otomatik olarak plan gönderme adımına geçersiniz.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        {createdTest ? (
          <DialogActions>
            <Button onClick={closeCreate} disabled={sendingPlan}>Atla</Button>
            <Button variant="contained" color="success" onClick={handleSendCreatedPlan} disabled={sendingPlan}>
              Planı Gönder
            </Button>
          </DialogActions>
        ) : (
          <DialogActions>
            <Button onClick={closeCreate} disabled={creating}>Vazgeç</Button>
            <Button variant="contained" onClick={handleCreateTest} disabled={creating}>
              Oluştur
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <Dialog open={showEdit} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle>Testi Düzenle</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Test başlığı"
              required
              fullWidth
              value={editForm.title}
              onChange={(e) => {
                const nextValue = e.target.value;
                setEditForm((prev) => ({ ...prev, title: nextValue }));
                setEditErrors((prev) => ({ ...prev, title: nextValue.trim() ? null : prev.title }));
              }}
              error={Boolean(editErrors.title)}
              helperText={editErrors.title || ''}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
              <DatePicker
                label="Tarih"
                value={toPickerDate(editForm.exam_date || '')}
                onChange={(value) => {
                  const nextValue = toIsoDate(value);
                  setEditForm((prev) => ({ ...prev, exam_date: nextValue }));
                  setEditErrors((prev) => ({ ...prev, exam_date: nextValue ? null : prev.exam_date }));
                }}
                format="dd.MM.yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: Boolean(editErrors.exam_date),
                    helperText: editErrors.exam_date || '',
                    sx: { minWidth: 160 },
                  },
                }}
              />
              <Autocomplete
                fullWidth
                options={classes}
                value={selectedEditClass}
                onChange={(_, value) => {
                  setEditForm((prev) => ({ ...prev, class_id: value?.id ?? null }));
                  setEditErrors((prev) => ({ ...prev, class_id: value?.id ? null : prev.class_id }));
                }}
                isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                getOptionLabel={(opt) => String(opt?.name ?? '')}
                renderInput={(params) => <TextField {...params} label="Şube" error={Boolean(editErrors.class_id)} helperText={editErrors.class_id || ''} />}
                disabled={hasAnyResultValue}
              />
            </Stack>
            {hasAnyResultValue && (
              <Alert severity="info" variant="outlined">
                Sonuç girilmişse şube değiştirilemez.
              </Alert>
            )}
            <TextField
              label="Süre (sn)"
              fullWidth
              value={editForm.duration_seconds}
              onChange={(e) => setEditForm((prev) => ({ ...prev, duration_seconds: e.target.value }))}
            />
            <TextField
              label="Not"
              fullWidth
              multiline
              minRows={3}
              value={editForm.notes}
              onChange={(e) => setEditForm((prev) => ({ ...prev, notes: e.target.value }))}
            />
            <FormControlLabel
              control={<Switch checked={!!editForm.reminder_enabled} onChange={(e) => setEditForm((prev) => ({ ...prev, reminder_enabled: e.target.checked }))} />}
              label="1 gün önce hatırlatma mesajı gönder"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={editing}>Vazgeç</Button>
          <Button variant="contained" onClick={handleUpdateTest} disabled={editing}>
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showDelete} onClose={closeDelete} fullWidth maxWidth="xs">
        <DialogTitle>Testi Sil</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Seçili test ve tüm sonuçları silinecek. Bu işlem geri alınamaz.
          </Typography>
          {detail.test && (
            <Paper variant="outlined" sx={{ p: 1.5, mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {detail.test.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {detail.test.exam_date ? humanDate(detail.test.exam_date) : '-'}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete} disabled={deleting}>Vazgeç</Button>
          <Button color="error" variant="contained" onClick={handleDeleteTest} disabled={deleting}>
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </LocalizationProvider>
  );
}
